/**
 * Resolves a selected issue entity (full Issue or wiki suggestion) to a persisted Issue.
 * Deduplicates concurrent processing of the same entity reference.
 */
import { useEffect, useRef, useState } from "react";
import { Issue } from "util/constants";
import { Suggestion } from "components/Issues/IssueSearch";
import { useIssueFromSuggestion } from "hooks/useIssueFromSuggestion";

export type IssueEntityInput = Issue | Suggestion | null;

export interface UseResolvedIssueResult {
  /** Resolved Issue when ready, or null if none / still resolving */
  resolvedIssue: Issue | null;
  /** True while ensureIssue is in flight */
  isResolving: boolean;
}

/**
 * Hydrates `entity` into a real Issue: uses existing Issue when id/cname/wikipedia_link
 * are present; otherwise calls ensureIssue for wiki suggestions.
 *
 * @param entity - Issue, Suggestion, or null
 */
export function useResolvedIssue(entity: IssueEntityInput): UseResolvedIssueResult {
  const { ensureIssue } = useIssueFromSuggestion();
  const [resolvedIssue, setResolvedIssue] = useState<Issue | null>(null);
  const [isResolving, setIsResolving] = useState(false);
  const processingEntityRef = useRef<IssueEntityInput | undefined>(undefined);

  useEffect(() => {
    if (!entity) {
      setResolvedIssue(null);
      processingEntityRef.current = null;
      setIsResolving(false);
      return;
    }

    if (processingEntityRef.current === entity) {
      return;
    }

    processingEntityRef.current = entity;

    const e = entity as Record<string, unknown>;
    const numericId = typeof e.id === "number" ? e.id : Number.parseInt(String(e.id || ""), 10);
    const hasStableIssueIdentity = Number.isFinite(numericId) && numericId > 0;
    if (hasStableIssueIdentity && (typeof e.name === "string" || typeof e.cname === "string" || typeof e.wikipedia_link === "string")) {
      setResolvedIssue(entity as Issue);
      setIsResolving(false);
      return;
    }

    setIsResolving(true);
    void (async () => {
      try {
        const createdIssue = await ensureIssue(entity as Suggestion);
        setResolvedIssue(createdIssue);
      } finally {
        setIsResolving(false);
      }
    })();
  }, [entity, ensureIssue]);

  return { resolvedIssue, isResolving };
}
